/**
 * The saved lab report on the lab desk and its Amend (PROD-3204, PM slice 5, boards N5 and N6). Pure helpers: which
 * version is shown, the words of each cell, and the complete next version an Amend sends.
 */
import { droppedAt } from '@/intake/lib/inbox-view';
import type {
  AmendLabReportRequest,
  LabObservationInput,
  LabReportDto,
  LabReportObservationDto,
  LabRequestTestDto,
} from '@/types/lab-results-api';

export const AMEND_REASON_MAX = 500;

/** Statuses of a report that can no longer be amended. */
const CLOSED = new Set(['cancelled', 'entered-in-error']);

/** The current version: the one the server marks current, else the highest version. */
export function currentReport(reports: LabReportDto[] | null | undefined): LabReportDto | null {
  const list = reports ?? [];
  const marked = list.filter((r) => r.isCurrent !== false);
  const pool = marked.length > 0 ? marked : list;
  return pool.reduce<LabReportDto | null>((best, r) => (!best || r.version > best.version ? r : best), null);
}

export const canAmend = (r: LabReportDto) => r.isCurrent !== false && !CLOSED.has((r.status ?? '').toLowerCase());

const STATUS_LABEL: Record<string, string> = {
  final: 'Final',
  amended: 'Amended',
  corrected: 'Corrected',
  registered: 'Registered',
  partial: 'Partial',
  preliminary: 'Preliminary',
  cancelled: 'Cancelled',
  'entered-in-error': 'Entered in error',
};

/** "Final · version 1", "Amended · version 2". */
export function versionLabel(r: LabReportDto): string {
  const s = (r.status ?? 'final').toLowerCase();
  return `${STATUS_LABEL[s] ?? r.status} · version ${r.version}`;
}

const at = (iso: string | null | undefined) => (iso ? droppedAt(iso) : null);

/**
 * Where the values came from and who stood behind them:
 * "Extracted from report · confirmed by Rania Saeed, 10:12a, Oct 20, 2026". A name the server has not sent is left out.
 */
export function provenanceLine(r: LabReportDto): string {
  const source = (r.source ?? '').toLowerCase();
  if (source === 'extracted') {
    const when = at(r.confirmedAt);
    const who = r.confirmedByName?.trim();
    const confirmed = who ? `confirmed by ${who}` : 'confirmed';
    return when ? `Extracted from report · ${confirmed}, ${when}` : `Extracted from report · ${confirmed}`;
  }
  if (source === 'interface') return 'Received from the lab system';
  const when = at(r.createdDate);
  const who = r.createdByName?.trim();
  const entered = who ? `Entered by hand by ${who}` : 'Entered by hand';
  return when ? `${entered}, ${when}` : entered;
}

/** The report's printed date, or "Not printed" when the server stamped its own time. */
export function reportedText(r: LabReportDto): string {
  if (r.issuedAtBasis === 'ServerTime' || !r.issuedAt) return 'Not printed';
  return at(r.issuedAt) ?? 'Not printed';
}

/** The specimen time of the first value that has one, else "Not printed". */
export function collectedText(r: LabReportDto): string {
  const o = r.observations.find((x) => x.effectiveAtBasis === 'Specimen' && x.effectiveAt);
  return (o?.effectiveAt && at(o.effectiveAt)) || 'Not printed';
}

export function performingLabText(r: LabReportDto): string {
  return r.performingLabName?.trim() || r.labVendorName?.trim() || 'Not printed';
}

export function testName(o: LabReportObservationDto): string {
  return o.display?.trim() || o.loinc || o.localCode || 'Result';
}

/** The value as printed when there is one ("<0.5"), else the stored value. */
export function valueText(o: LabReportObservationDto): string {
  if (o.valuePrinted?.trim()) return o.valuePrinted.trim();
  if (o.valueNumeric !== null && o.valueNumeric !== undefined) return `${o.valueComparator ?? ''}${o.valueNumeric}`;
  return o.valueText?.trim() || o.valueCodedDisplay?.trim() || '—';
}

export function unitText(o: LabReportObservationDto): string {
  return o.unitText?.trim() || o.unit?.trim() || '';
}

/** The range as printed, else built from the bounds. */
export function rangeText(o: LabReportObservationDto): string {
  if (o.refText?.trim()) return o.refText.trim();
  const low = o.refLow ?? null;
  const high = o.refHigh ?? null;
  if (low !== null && high !== null) return `${low}–${high}`;
  if (high !== null) return `≤ ${high}`;
  if (low !== null) return `≥ ${low}`;
  return '';
}

/** The flag as printed, else a flag the server set that is not normal. */
export function flagText(o: LabReportObservationDto): string {
  if (o.interpretationText?.trim()) return o.interpretationText.trim();
  const f = o.interpretation?.trim() ?? '';
  return f === '' || f === 'N' ? '' : f;
}

export interface ReportGroup {
  key: string;
  title: string;
  rows: LabReportObservationDto[];
}

/**
 * The values grouped under the ordered test they answer, in the order of the tests. A value with no test goes under the
 * section the lab printed it in ("Haematology", "Clinical Chemistry"; Patient #1188), in the order the sections first
 * appear, the same grouping as the review page. The rest come last, under "Other results" (or "Results" when they are all).
 */
export function reportGroups(r: LabReportDto, tests: LabRequestTestDto[] | null | undefined): ReportGroup[] {
  const rows = r.observations.filter((o) => o.isCurrent !== false);
  const groups: ReportGroup[] = [];
  for (const t of tests ?? []) {
    const own = rows.filter((o) => o.patientLabRequestTestId === t.id);
    if (own.length > 0) groups.push({ key: t.id, title: t.testDisplay?.trim() || t.testCode || 'Test', rows: own });
  }
  const known = new Set(groups.map((g) => g.key));
  const unlinked = rows.filter((o) => !o.patientLabRequestTestId || !known.has(o.patientLabRequestTestId));
  // By printed section: one group per section, matched without case or extra spaces, titled as first printed.
  const sections = new Map<string, ReportGroup>();
  const other: LabReportObservationDto[] = [];
  for (const o of unlinked) {
    const title = o.sectionPrinted?.replace(/\s+/g, ' ').trim();
    if (!title) {
      other.push(o);
      continue;
    }
    const key = `section:${title.toLowerCase()}`;
    const group = sections.get(key);
    if (group) group.rows.push(o);
    else sections.set(key, { key, title, rows: [o] });
  }
  groups.push(...sections.values());
  if (other.length > 0) groups.push({ key: 'other', title: groups.length > 0 ? 'Other results' : 'Results', rows: other });
  return groups;
}

export const resultsCount = (n: number) => `${n} ${n === 1 ? 'result' : 'results'}`;

export type ParsedValue = { kind: 'numeric'; value: number; comparator: string | null } | { kind: 'text'; value: string };

/**
 * The new value typed in the Amend dialog. A value that was a number stays a number ("321", "<0.5", "5,9"); a text
 * value stays text. Null when it cannot be read.
 */
export function parseAmendValue(raw: string, o: LabReportObservationDto): ParsedValue | null {
  const s = raw.trim();
  if (s === '') return null;
  if (o.valueNumeric === null || o.valueNumeric === undefined) return { kind: 'text', value: s };
  const m = /^(<=|>=|<|>)?\s*(-?\d+(?:[.,]\d+)?)$/.exec(s);
  if (!m) return null;
  const value = Number(m[2].replace(',', '.'));
  return Number.isFinite(value) ? { kind: 'numeric', value, comparator: m[1] ?? null } : null;
}

/** The saved value as a text value is read: the text, else the coded value's display. */
const savedText = (o: LabReportObservationDto) => (o.valueText?.trim() || o.valueCodedDisplay?.trim() || '');

/** True when `observationId` is a current value of `r` (false once a newer version replaced it). */
export const isCurrentValue = (r: LabReportDto, observationId: string) =>
  r.observations.some((o) => o.id === observationId && o.isCurrent !== false);

/** True when the typed value is the value already saved. */
export function sameValue(p: ParsedValue, o: LabReportObservationDto): boolean {
  if (p.kind === 'text') return p.value === savedText(o);
  return p.value === o.valueNumeric && (p.comparator ?? null) === (o.valueComparator ?? null);
}

function input(o: LabReportObservationDto): LabObservationInput {
  return {
    patientLabRequestTestId: o.patientLabRequestTestId ?? null,
    resultComponentId: o.resultComponentId ?? null,
    loinc: o.loinc,
    localCode: o.localCode ?? null,
    localSystem: o.localSystem ?? null,
    scripsComponentCode: o.scripsComponentCode ?? null,
    scripsComponentSystem: o.scripsComponentSystem ?? null,
    display: o.display ?? null,
    valueNumeric: o.valueNumeric,
    valueComparator: o.valueComparator ?? null,
    valueText: o.valueText ?? null,
    valueCodedCode: o.valueCodedCode ?? null,
    valueCodedDisplay: o.valueCodedDisplay ?? null,
    unit: o.unit,
    unitText: o.unitText ?? null,
    refLow: o.refLow ?? null,
    refHigh: o.refHigh ?? null,
    refText: o.refText ?? null,
    // A flag the server computed is computed again from the new value; a flag the lab printed is kept.
    interpretation: o.interpretationSource === 'Reported' ? (o.interpretation ?? null) : null,
    interpretationText: o.interpretationText ?? null,
    effectiveAt: o.effectiveAt ?? null,
    extractionRowId: o.extractionRowId ?? null,
  };
}

/**
 * The complete next version: every current value of `r` as it is, except `changedId`, which takes `next`. The report
 * fields are sent again because the server copies nothing from the version before. A date the server stamped itself
 * (ServerTime) is left out, so it stays "not printed".
 *
 * The changed value drops what the lab printed for the old value, the same rule as Patient's carry-over (a changed
 * value gets no printed value): its printed flag ("L" for 312 may be wrong for 321; for a report that was not read
 * from a document the server computes the flag again from the range), and a coded value it replaces as text.
 * Throws when `changedId` is not a current value of `r` (a stale form after another amend landed).
 */
export function buildAmendBody(
  r: LabReportDto,
  changedId: string,
  next: ParsedValue,
  reason: string,
): AmendLabReportRequest {
  if (!isCurrentValue(r, changedId)) throw new Error('amend_stale_value');
  const current = r.observations.filter((o) => o.isCurrent !== false);
  const observations = current.map((o) => {
    const row = input(o);
    if (o.id !== changedId) return row;
    const changed = { ...row, interpretation: null, interpretationText: null };
    // One value kind per row: the new value clears the other kinds, so they never disagree.
    return next.kind === 'numeric'
      ? { ...changed, valueNumeric: next.value, valueComparator: next.comparator, valueText: null, valueCodedCode: null, valueCodedDisplay: null }
      : { ...changed, valueText: next.value, valueNumeric: null, valueComparator: null, valueCodedCode: null, valueCodedDisplay: null };
  });
  const body: AmendLabReportRequest = {
    status: 'amended',
    patientDocumentId: r.patientDocumentId ?? null,
    conclusion: r.conclusion ?? null,
    source: r.source ?? 'Transcribed',
    performingLabName: r.performingLabName ?? null,
    extractionRunId: r.extractionRunId ?? null,
    attestationVersion: r.attestationVersion ?? null,
    reason: reason.trim(),
    observations,
  };
  if (r.issuedAt && r.issuedAtBasis !== 'ServerTime') body.issuedAt = r.issuedAt;
  return body;
}
